import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users, MessageSquare, Car, Calendar, FileText, Flag,
  Shield, Megaphone, Search, Bell
} from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import { UsersTab } from '../components/admin/UsersTab';
import { TopicsTab } from '../components/admin/TopicsTab';
import { VehiclesTab } from '../components/admin/VehiclesTab';
import { ReportsTab } from '../components/admin/ReportsTab';
import { NewsTab } from '../components/admin/NewsTab';
import { AdsTab } from '../components/admin/AdsTab';
import { CategoriesTab } from '../components/admin/CategoriesTab';
import { NotificationsTab } from '../components/admin/NotificationsTab';
import { PagesTab } from '../components/admin/PagesTab';
import { SiteSettingsTab } from '../components/admin/SiteSettingsTab';
import { SeoTab } from '../components/admin/SeoTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function StatBox({ label, value, icon: Icon, color }) {
  return (
    <div className={`rounded-2xl p-5 ${color}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-7 h-7" />
        <div>
          <div className="text-2xl font-extrabold">{value}</div>
          <div className="text-sm opacity-80">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [reports, setReports] = useState([]);
  const [news, setNews] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  // New vehicle form
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const emptyVehicleForm = {
    brand: '', model: '', category: 'L6e', year: 2024,
    max_speed: 0, range_km: 0, battery_kwh: 0, power_kw: 0,
    weight_kg: 0, seats: 2, license_required: '', price_min: 0, price_max: 0,
    image_url: '', description: ''
  };
  const [vehicleForm, setVehicleForm] = useState(emptyVehicleForm);
  const [vehicleSubmitting, setVehicleSubmitting] = useState(false);

  const openAddVehicle = () => {
    setEditingVehicleId(null);
    setVehicleForm(emptyVehicleForm);
    setShowVehicleForm(true);
  };

  const openEditVehicle = (v) => {
    setEditingVehicleId(v._id);
    setVehicleForm({
      brand: v.brand || '', model: v.model || '', category: v.category || 'L6e', year: v.year || 2024,
      max_speed: v.max_speed || 0, range_km: v.range_km || 0, battery_kwh: v.battery_kwh || 0, power_kw: v.power_kw || 0,
      weight_kg: v.weight_kg || 0, seats: v.seats || 2, license_required: v.license_required || '',
      price_min: v.price_min || 0, price_max: v.price_max || 0,
      image_url: v.image_url || '', description: v.description || ''
    });
    setShowVehicleForm(true);
  };

  // News form
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [newsForm, setNewsForm] = useState({ title: '', content: '', image_url: '', category: 'genel' });
  const [newsSubmitting, setNewsSubmitting] = useState(false);

  // Ads
  const [ads, setAds] = useState([]);
  const [adPlacements, setAdPlacements] = useState([]);
  const [showAdForm, setShowAdForm] = useState(false);
  const [adForm, setAdForm] = useState({ title: '', placement: 'home_top', image_url: '', link_url: '', html_content: '', is_active: true });
  const [adImageFile, setAdImageFile] = useState(null);
  const [adSubmitting, setAdSubmitting] = useState(false);

  // SEO
  const [seoForm, setSeoForm] = useState(null);
  const [seoSaving, setSeoSaving] = useState(false);

  // Forum Categories
  const [categories, setCategories] = useState([]);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', color: 'emerald', order: 0 });
  const [catSubmitting, setCatSubmitting] = useState(false);

  // User edit
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState(null);
  const [userSaving, setUserSaving] = useState(false);

  // Notifications broadcast
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifUrl, setNotifUrl] = useState('/');
  const [notifSending, setNotifSending] = useState(false);

  // Site pages
  const [sitePages, setSitePages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [pageForm, setPageForm] = useState({ slug: '', title: '', content: '', is_published: true });
  const [pageSaving, setPageSaving] = useState(false);

  // Site settings
  const [siteSettings, setSiteSettings] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);

  useEffect(() => {
    if (user === undefined) return; // still loading
    if (!user) { navigate('/giris'); return; }
    if (!isAdmin) { navigate('/'); toast.error('Bu sayfaya erişim yetkiniz yok'); return; }
    fetchAll();
  }, [user, isAdmin]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsR, usersR, topicsR, reportsR, newsR, vehiclesR, adsR, seoR, catsR, pagesR, siteR] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users?limit=20`, { withCredentials: true }),
        axios.get(`${API}/admin/topics?limit=20`, { withCredentials: true }),
        axios.get(`${API}/admin/reports?status=pending`, { withCredentials: true }),
        axios.get(`${API}/news`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/admin/ads`, { withCredentials: true }),
        axios.get(`${API}/seo`),
        axios.get(`${API}/forum/categories`),
        axios.get(`${API}/admin/site/pages`, { withCredentials: true }),
        axios.get(`${API}/site/settings`),
      ]);
      setStats(statsR.data);
      setUsers(usersR.data.users || []);
      setTopics(topicsR.data.topics || []);
      setReports(reportsR.data.reports || []);
      setNews(newsR.data.news || []);
      setVehicles(vehiclesR.data.vehicles || []);
      setAds(adsR.data.ads || []);
      setAdPlacements(adsR.data.placements || []);
      setSeoForm(seoR.data.seo || {});
      setCategories(catsR.data.categories || []);
      setSitePages(pagesR.data.pages || []);
      setSiteSettings(siteR.data.settings || {});
    } finally {
      setLoading(false);
    }
  };

  const handleUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role }, { withCredentials: true });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role } : u));
      toast.success('Kullanıcı rolü güncellendi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      username: u.username || '',
      display_name: u.display_name || '',
      email: u.email || '',
      bio: u.bio || '',
      avatar_url: u.avatar_url || '',
      new_password: '',
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setUserSaving(true);
    try {
      const payload = { ...userForm };
      const newPw = (payload.new_password || '').trim();
      if (newPw) {
        if (newPw.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); setUserSaving(false); return; }
        payload.new_password = newPw;
      } else {
        delete payload.new_password;
      }
      await axios.put(`${API}/admin/users/${editingUser._id}`, payload, { withCredentials: true });
      // password_hash state'te tutulmuyor; UI'da sadece diğer alanları güncelle
      const { new_password, ...uiFields } = payload;
      setUsers(prev => prev.map(u => u._id === editingUser._id ? { ...u, ...uiFields } : u));
      if (newPw) {
        toast.success(`Şifre değiştirildi — kullanıcı artık "${newPw}" şifresiyle giriş yapabilir`, { duration: 8000 });
      } else {
        toast.success('Kullanıcı güncellendi');
      }
      setEditingUser(null);
    } catch (err) { toast.error(formatError(err)); }
    finally { setUserSaving(false); }
  };

  const handleToggleBan = async (u) => {
    try {
      await axios.put(`${API}/admin/users/${u._id}`, { is_banned: !u.is_banned }, { withCredentials: true });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, is_banned: !u.is_banned } : x));
      toast.success(!u.is_banned ? 'Kullanıcı yasaklandı' : 'Yasak kaldırıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`${u.display_name || u.username} ve TÜM içeriği silinecek. Devam?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u._id}`, { withCredentials: true });
      setUsers(prev => prev.filter(x => x._id !== u._id));
      toast.success('Kullanıcı silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  // Forum categories
  const openCategoryForm = (cat = null) => {
    setEditingCat(cat);
    setCatForm(cat ? { name: cat.name, description: cat.description || '', color: cat.color || 'emerald', order: cat.order || 0 } : { name: '', description: '', color: 'emerald', order: 0 });
    setShowCatForm(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setCatSubmitting(true);
    try {
      if (editingCat) {
        const r = await axios.put(`${API}/admin/forum/categories/${editingCat.slug}`, catForm, { withCredentials: true });
        setCategories(prev => prev.map(c => c.slug === editingCat.slug ? r.data.category : c));
        toast.success('Kategori güncellendi');
      } else {
        const r = await axios.post(`${API}/admin/forum/categories`, catForm, { withCredentials: true });
        setCategories(prev => [...prev, r.data.category]);
        toast.success('Kategori oluşturuldu');
      }
      setShowCatForm(false);
      setEditingCat(null);
    } catch (err) { toast.error(formatError(err)); }
    finally { setCatSubmitting(false); }
  };

  const handleDeleteCategory = async (slug) => {
    if (!window.confirm('Kategoriyi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/forum/categories/${slug}`, { withCredentials: true });
      setCategories(prev => prev.filter(c => c.slug !== slug));
      toast.success('Kategori silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notifTitle.trim() || !notifBody.trim()) {
      toast.error('Başlık ve içerik zorunlu');
      return;
    }
    setNotifSending(true);
    try {
      const r = await axios.post(`${API}/admin/push/broadcast`, { title: notifTitle, body: notifBody, url: notifUrl || '/' }, { withCredentials: true });
      const { sent = 0, total = 0, sent_to_users = 0, errors = [] } = r.data;
      if (total === 0) toast.error('Bildirim izni vermiş abone yok');
      else if (sent === 0) toast.error(`Hiçbir cihaza iletilemedi (${total} abonelik). ${errors[0] || ''}`);
      else toast.success(`${sent}/${total} cihaza gönderildi (${sent_to_users} kullanıcı)`);
      if (sent > 0) { setNotifTitle(''); setNotifBody(''); setNotifUrl('/'); }
    } catch (err) { toast.error(formatError(err)); }
    finally { setNotifSending(false); }
  };

  // Site pages
  const openPageForm = (p = null) => {
    setEditingPage(p);
    setPageForm(p ? { slug: p.slug, title: p.title, content: p.content || '', is_published: p.is_published !== false } : { slug: '', title: '', content: '', is_published: true });
    setPageDialogOpen(true);
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    const slug = (pageForm.slug || '').trim();
    if (!slug) { toast.error('Slug zorunlu'); return; }
    setPageSaving(true);
    try {
      const r = await axios.put(`${API}/admin/site/pages/${slug}`, {
        title: pageForm.title, content: pageForm.content, is_published: pageForm.is_published,
      }, { withCredentials: true });
      setSitePages(prev => {
        const exists = prev.find(p => p.slug === slug);
        return exists ? prev.map(p => p.slug === slug ? r.data.page : p) : [...prev, r.data.page];
      });
      toast.success('Sayfa kaydedildi');
      setPageDialogOpen(false);
      setEditingPage(null);
    } catch (err) { toast.error(formatError(err)); }
    finally { setPageSaving(false); }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm('Sayfayı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/site/pages/${slug}`, { withCredentials: true });
      setSitePages(prev => prev.filter(p => p.slug !== slug));
      toast.success('Sayfa silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  // Site settings
  const handleSaveSite = async () => {
    setSiteSaving(true);
    try {
      const r = await axios.put(`${API}/admin/site/settings`, siteSettings, { withCredentials: true });
      setSiteSettings(r.data.settings || siteSettings);
      toast.success('Site ayarları kaydedildi');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSiteSaving(false); }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Konuyu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/forum/topics/${topicId}`, { withCredentials: true });
      setTopics(prev => prev.filter(t => t._id !== topicId));
      toast.success('Konu silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleToggleLock = async (topicId, isLocked) => {
    try {
      await axios.patch(`${API}/forum/topics/${topicId}`, { is_locked: !isLocked }, { withCredentials: true });
      setTopics(prev => prev.map(t => t._id === topicId ? { ...t, is_locked: !isLocked } : t));
      toast.success(!isLocked ? 'Konu kilitlendi' : 'Konu açıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleToggleTopicNoindex = async (topicId, noindex) => {
    try {
      await axios.patch(`${API}/forum/topics/${topicId}`, { noindex: !noindex }, { withCredentials: true });
      setTopics(prev => prev.map(t => t._id === topicId ? { ...t, noindex: !noindex } : t));
      toast.success(!noindex ? 'Konu arama motorlarına kapatıldı' : 'Konu arama motorlarına açıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleMoveTopic = async (topicId, newSlug) => {
    const topic = topics.find(t => t._id === topicId);
    if (!topic || !newSlug || topic.category_slug === newSlug) return;
    try {
      const r = await axios.post(`${API}/admin/topics/${topicId}/move`, { category_slug: newSlug }, { withCredentials: true });
      setTopics(prev => prev.map(t => t._id === topicId ? { ...t, category_slug: r.data.category_slug, category_name: r.data.category_name } : t));
      toast.success(`"${topic.title}" → ${r.data.category_name}`);
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleResolveReport = async (reportId) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}`, { status: 'resolved' }, { withCredentials: true });
      setReports(prev => prev.filter(r => r._id !== reportId));
      toast.success('Rapor çözümlendi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setVehicleSubmitting(true);
    try {
      if (editingVehicleId) {
        await axios.put(`${API}/vehicles/${editingVehicleId}`, vehicleForm, { withCredentials: true });
        setVehicles(prev => prev.map(v => v._id === editingVehicleId ? { ...v, ...vehicleForm } : v));
        toast.success('Araç güncellendi!');
      } else {
        const r = await axios.post(`${API}/vehicles`, vehicleForm, { withCredentials: true });
        setVehicles(prev => [...prev, r.data.vehicle]);
        toast.success('Araç eklendi!');
      }
      setShowVehicleForm(false);
      setEditingVehicleId(null);
      setVehicleForm(emptyVehicleForm);
    } catch (err) { toast.error(formatError(err)); }
    finally { setVehicleSubmitting(false); }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Aracı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { withCredentials: true });
      setVehicles(prev => prev.filter(v => v._id !== vehicleId));
      toast.success('Araç silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    setNewsSubmitting(true);
    try {
      const r = await axios.post(`${API}/news`, newsForm, { withCredentials: true });
      setNews(prev => [r.data.news, ...prev]);
      setShowNewsForm(false);
      setNewsForm({ title: '', content: '', image_url: '', category: 'genel' });
      toast.success('Haber eklendi!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setNewsSubmitting(false); }
  };

  const handleDeleteNews = async (newsId) => {
    try {
      await axios.delete(`${API}/news/${newsId}`, { withCredentials: true });
      setNews(prev => prev.filter(n => n._id !== newsId));
      toast.success('Haber silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadAdImage = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const r = await axios.post(`${API}/upload-attachment`, form, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data.url;
  };

  const handleAddAd = async (e) => {
    e.preventDefault();
    setAdSubmitting(true);
    try {
      let payload = { ...adForm };
      if (adImageFile) {
        payload.image_url = await uploadAdImage(adImageFile);
      }
      if (!payload.image_url && !payload.html_content) {
        toast.error('Görsel yükle veya HTML içerik gir');
        setAdSubmitting(false);
        return;
      }
      const r = await axios.post(`${API}/admin/ads`, payload, { withCredentials: true });
      setAds(prev => [r.data.ad, ...prev]);
      setShowAdForm(false);
      setAdForm({ title: '', placement: 'home_top', image_url: '', link_url: '', html_content: '', is_active: true });
      setAdImageFile(null);
      toast.success('Reklam eklendi!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setAdSubmitting(false); }
  };

  const handleToggleAd = async (adId, isActive) => {
    try {
      const r = await axios.put(`${API}/admin/ads/${adId}`, { is_active: !isActive }, { withCredentials: true });
      setAds(prev => prev.map(a => a._id === adId ? r.data.ad : a));
      toast.success(!isActive ? 'Reklam aktifleştirildi' : 'Reklam pasifleştirildi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Reklamı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, { withCredentials: true });
      setAds(prev => prev.filter(a => a._id !== adId));
      toast.success('Reklam silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleSeoSave = async () => {
    setSeoSaving(true);
    try {
      // Clean up: convert noindex_paths string to array if needed
      const payload = { ...seoForm };
      if (typeof payload.noindex_paths === 'string') {
        payload.noindex_paths = payload.noindex_paths.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      }
      await axios.put(`${API}/admin/seo`, payload, { withCredentials: true });
      toast.success('SEO ayarları kaydedildi');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSeoSaving(false); }
  };

  const handleSeoUpload = async (file, field) => {
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/upload-attachment`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Build full URL
      const url = `${API}/files/${r.data.url}`;
      setSeoForm(prev => ({ ...prev, [field]: url }));
      toast.success("Yüklendi, Kaydet'e basmayı unutma");
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!isAdmin) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Admin Paneli" noindex />
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
          <Shield className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Paneli</h1>
          <p className="text-slate-500 text-sm">Platform yönetimi</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatBox label="Üyeler" value={stats.users || 0} icon={Users} color="bg-emerald-50 text-emerald-700" />
        <StatBox label="Konular" value={stats.topics || 0} icon={MessageSquare} color="bg-blue-50 text-blue-700" />
        <StatBox label="Yanıtlar" value={stats.replies || 0} icon={MessageSquare} color="bg-violet-50 text-violet-700" />
        <StatBox label="Araçlar" value={stats.vehicles || 0} icon={Car} color="bg-orange-50 text-orange-700" />
        <StatBox label="Etkinlikler" value={stats.events || 0} icon={Calendar} color="bg-cyan-50 text-cyan-700" />
        <StatBox label="Raporlar" value={stats.reports || 0} icon={Flag} color="bg-red-50 text-red-700" />
      </div>

      <Tabs defaultValue="users" data-testid="admin-tabs">
        <TabsList className="mb-6 flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-3.5 h-3.5 mr-1" />Üyeler</TabsTrigger>
          <TabsTrigger value="topics" data-testid="admin-tab-topics"><MessageSquare className="w-3.5 h-3.5 mr-1" />Konular</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles"><Car className="w-3.5 h-3.5 mr-1" />Araçlar</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">
            <Flag className="w-3.5 h-3.5 mr-1" />Raporlar
            {reports.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">{reports.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="news" data-testid="admin-tab-news"><FileText className="w-3.5 h-3.5 mr-1" />Haberler</TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads"><Megaphone className="w-3.5 h-3.5 mr-1" />Reklamlar</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories"><FileText className="w-3.5 h-3.5 mr-1" />Kategoriler</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications"><Bell className="w-3.5 h-3.5 mr-1" />Bildirim</TabsTrigger>
          <TabsTrigger value="pages" data-testid="admin-tab-pages"><FileText className="w-3.5 h-3.5 mr-1" />Sayfalar</TabsTrigger>
          <TabsTrigger value="site" data-testid="admin-tab-site"><Shield className="w-3.5 h-3.5 mr-1" />Site Ayarları</TabsTrigger>
          <TabsTrigger value="seo" data-testid="admin-tab-seo"><Search className="w-3.5 h-3.5 mr-1" />SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <UsersTab
            users={users}
            handleUserRole={handleUserRole}
            openEditUser={openEditUser}
            handleToggleBan={handleToggleBan}
            handleDeleteUser={handleDeleteUser}
          />
        </TabsContent>

        <TabsContent value="topics">
          <TopicsTab
            topics={topics}
            categories={categories}
            handleMoveTopic={handleMoveTopic}
            handleToggleTopicNoindex={handleToggleTopicNoindex}
            handleToggleLock={handleToggleLock}
            handleDeleteTopic={handleDeleteTopic}
          />
        </TabsContent>

        <TabsContent value="vehicles">
          <VehiclesTab
            showVehicleForm={showVehicleForm}
            setShowVehicleForm={setShowVehicleForm}
            setEditingVehicleId={setEditingVehicleId}
            setVehicleForm={setVehicleForm}
            emptyVehicleForm={emptyVehicleForm}
            openAddVehicle={openAddVehicle}
            editingVehicleId={editingVehicleId}
            handleAddVehicle={handleAddVehicle}
            vehicleForm={vehicleForm}
            vehicleSubmitting={vehicleSubmitting}
            vehicles={vehicles}
            openEditVehicle={openEditVehicle}
            handleDeleteVehicle={handleDeleteVehicle}
          />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsTab reports={reports} handleResolveReport={handleResolveReport} />
        </TabsContent>

        <TabsContent value="news">
          <NewsTab
            showNewsForm={showNewsForm}
            setShowNewsForm={setShowNewsForm}
            handleAddNews={handleAddNews}
            newsForm={newsForm}
            setNewsForm={setNewsForm}
            newsSubmitting={newsSubmitting}
            news={news}
            handleDeleteNews={handleDeleteNews}
          />
        </TabsContent>

        <TabsContent value="ads">
          <AdsTab
            showAdForm={showAdForm}
            setShowAdForm={setShowAdForm}
            handleAddAd={handleAddAd}
            adForm={adForm}
            setAdForm={setAdForm}
            adPlacements={adPlacements}
            setAdImageFile={setAdImageFile}
            adSubmitting={adSubmitting}
            ads={ads}
            handleToggleAd={handleToggleAd}
            handleDeleteAd={handleDeleteAd}
          />
        </TabsContent>

        <TabsContent value="categories">
          <CategoriesTab
            openCategoryForm={openCategoryForm}
            categories={categories}
            handleDeleteCategory={handleDeleteCategory}
          />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab
            handleSendNotification={handleSendNotification}
            notifTitle={notifTitle}
            setNotifTitle={setNotifTitle}
            notifBody={notifBody}
            setNotifBody={setNotifBody}
            notifUrl={notifUrl}
            setNotifUrl={setNotifUrl}
            notifSending={notifSending}
          />
        </TabsContent>

        <TabsContent value="pages">
          <PagesTab
            openPageForm={openPageForm}
            sitePages={sitePages}
            handleDeletePage={handleDeletePage}
          />
        </TabsContent>

        <TabsContent value="site">
          <SiteSettingsTab
            siteSettings={siteSettings}
            setSiteSettings={setSiteSettings}
            handleSaveSite={handleSaveSite}
            siteSaving={siteSaving}
          />
        </TabsContent>

        <TabsContent value="seo">
          <SeoTab
            seoForm={seoForm}
            setSeoForm={setSeoForm}
            handleSeoUpload={handleSeoUpload}
            handleSeoSave={handleSeoSave}
            seoSaving={seoSaving}
          />
        </TabsContent>
      </Tabs>

      {/* USER EDIT DIALOG */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        <DialogContent className="max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Kullanıcıyı Düzenle</DialogTitle>
            <DialogDescription>Tüm alanlar admin tarafından değiştirilebilir.</DialogDescription>
          </DialogHeader>
          {userForm && (
            <form onSubmit={handleSaveUser} className="space-y-3 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Kullanıcı adı</Label><Input value={userForm.username} onChange={e => setUserForm(p => ({...p, username: e.target.value}))} className="mt-1" data-testid="edit-user-username" /></div>
                <div><Label>Görünen Ad</Label><Input value={userForm.display_name} onChange={e => setUserForm(p => ({...p, display_name: e.target.value}))} className="mt-1" data-testid="edit-user-display" /></div>
              </div>
              <div><Label>E-posta</Label><Input type="email" value={userForm.email} onChange={e => setUserForm(p => ({...p, email: e.target.value}))} className="mt-1" data-testid="edit-user-email" /></div>
              <div><Label>Avatar URL</Label><Input value={userForm.avatar_url} onChange={e => setUserForm(p => ({...p, avatar_url: e.target.value}))} className="mt-1" data-testid="edit-user-avatar" /></div>
              <div><Label>Hakkında</Label><Textarea value={userForm.bio} onChange={e => setUserForm(p => ({...p, bio: e.target.value}))} rows={2} className="mt-1 resize-none" data-testid="edit-user-bio" /></div>
              <div><Label>Yeni Şifre (opsiyonel)</Label><Input type="password" autoComplete="new-password" value={userForm.new_password} onChange={e => setUserForm(p => ({...p, new_password: e.target.value}))} className="mt-1" placeholder="Boş bırakırsan değişmez" data-testid="edit-user-password" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>İptal</Button>
                <Button type="submit" disabled={userSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-user-save-btn">{userSaving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* PAGE EDIT DIALOG */}
      <Dialog open={pageDialogOpen} onOpenChange={(o) => { setPageDialogOpen(o); if (!o) setEditingPage(null); }}>
        <DialogContent className="max-w-xl" data-testid="page-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingPage?.slug ? 'Sayfayı Düzenle' : 'Yeni Sayfa'}</DialogTitle>
            <DialogDescription>Kullanım koşulları, gizlilik, çerez, iletişim gibi statik sayfalar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePage} className="space-y-3 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Slug (URL)</Label>
                <Input value={pageForm.slug} onChange={e => setPageForm(p => ({...p, slug: e.target.value}))} disabled={!!editingPage?.slug} className="mt-1" placeholder="kullanim-kosullari" required data-testid="page-form-slug" />
              </div>
              <div>
                <Label>Başlık</Label>
                <Input value={pageForm.title} onChange={e => setPageForm(p => ({...p, title: e.target.value}))} required className="mt-1" data-testid="page-form-title" />
              </div>
            </div>
            <div>
              <Label>İçerik</Label>
              <Textarea value={pageForm.content} onChange={e => setPageForm(p => ({...p, content: e.target.value}))} rows={10} className="mt-1 resize-none font-mono text-sm" data-testid="page-form-content" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pageForm.is_published} onChange={e => setPageForm(p => ({...p, is_published: e.target.checked}))} data-testid="page-form-published" />
              Yayında
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPageDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={pageSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="page-form-submit">{pageSaving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CATEGORY EDIT DIALOG */}
      <Dialog open={showCatForm} onOpenChange={setShowCatForm}>
        <DialogContent className="max-w-md" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingCat ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}</DialogTitle>
            <DialogDescription>Kategoriler forum konularının gruplandığı başlıklardır.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCategory} className="space-y-3 mt-2">
            <div><Label>Kategori Adı</Label><Input value={catForm.name} onChange={e => setCatForm(p => ({...p, name: e.target.value}))} required className="mt-1" data-testid="cat-form-name" /></div>
            <div><Label>Açıklama</Label><Textarea value={catForm.description} onChange={e => setCatForm(p => ({...p, description: e.target.value}))} rows={2} className="mt-1 resize-none" data-testid="cat-form-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Renk</Label>
                <Select value={catForm.color} onValueChange={v => setCatForm(p => ({...p, color: v}))}>
                  <SelectTrigger className="mt-1" data-testid="cat-form-color"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emerald">Yeşil</SelectItem>
                    <SelectItem value="sky">Mavi</SelectItem>
                    <SelectItem value="amber">Turuncu</SelectItem>
                    <SelectItem value="rose">Pembe</SelectItem>
                    <SelectItem value="violet">Mor</SelectItem>
                    <SelectItem value="slate">Gri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Sıra</Label><Input type="number" value={catForm.order} onChange={e => setCatForm(p => ({...p, order: parseInt(e.target.value) || 0}))} className="mt-1" data-testid="cat-form-order" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCatForm(false)}>İptal</Button>
              <Button type="submit" disabled={catSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cat-form-submit">{catSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
